import { SaleWithGroupReportDto, SellStationSummaryReport } from '@/__generated';
import AppDatePicker from '@/components/FormItems/DatePicker';
import { dateTimeDisplayFormat, formatApiDate, ROW_GUTTER } from '@/config';
import { formatCurrency } from '@/helpers/format';
import { apiSdk } from '@/httpClient';
import MainContent from '@/layouts/MainContent';
import { Button, Col, Form, Row, Space, Table } from 'antd';
import { t } from 'i18next';
import moment from 'moment';
import React from 'react';
import { Helmet } from 'react-helmet';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import { groupBy, orderBy, sumBy } from 'lodash';
import { exportService } from '@/services/exportService';
import { ExportExcelBtn, ExportPdfBtn } from '@/components/app/ExportBtn';

const PAGE_TITLE = 'Sale With Group Report';
interface SaleReportRowProps extends SaleWithGroupReportDto {
  isRowHeader?: boolean;
  isSummaryField?: boolean;
  childData?: SellStationSummaryReport[];
  isBlankField?: boolean;
}

const SaleWithGroupReportPage = () => {
  const [searchForm] = Form.useForm();
  const [tableKey, setTableKey] = React.useState<number>();
  const [report, setReport] = React.useState<SaleWithGroupReportDto[]>();
  const [loading, setIsLoading] = React.useState(false);
  const [filter, setFilter] = React.useState<{ fromDate: string; toDate: string }>({
    fromDate: formatApiDate(moment().add(-1, 'month')),
    toDate: formatApiDate(moment()),
  });

  React.useEffect(() => {
    setIsLoading(true);

    apiSdk.SaleWithGroupReportService.getReports({
      fromDate: filter?.fromDate,
      toDate: filter?.toDate,
    })
      .then(res => {
        setReport(res);
        setTableKey(new Date().getTime());
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [filter]);

  const data = React.useMemo(() => {
    if (!report?.length) {
      return [];
    }
    let reportData: SaleReportRowProps[] = [];
    let groupedData = groupBy(report, x => x.stationId);
    let keys = Object.keys(groupedData);
    keys.forEach(k => {
      let data = groupedData[k];
      reportData.push({
        isRowHeader: true,
        station: t('salesReport_StationGroup', { station: data.at(0)?.station, count: data.length }),
        stationId: k,
        childData: orderBy(data, x => moment(x.date)),
      });
      reportData.push({
        isSummaryField: true,
        amount: sumBy(data, x => x.amount || 0),
      });
    });
    reportData.push({ isBlankField: true });
    reportData.push({
      isSummaryField: true,
      amount: sumBy(report, x => x.amount || 0),
    });

    return reportData;
  }, [report]);

  const columns = React.useMemo(() => {
    return [
      {
        title: t('Station'),
        ellipsis: true,
        width: 120,
        dataIndex: 'station',
        onCell: r => ({ colSpan: r.isRowHeader || r.isBlankField ? 3 : r.isSummaryField ? 2 : 1 }),
      },
      {
        title: t('Date'),
        dataIndex: 'date',
        width: 200,
        render: v => dateTimeDisplayFormat.dateOnly(v),
        ellipsis: true,
        onCell: r => ({ colSpan: r.isRowHeader || r.isSummaryField || r.isBlankField ? 0 : 1 }),
      },
      {
        title: t('Amount'),
        dataIndex: 'amount',
        width: 200,
        render: (v, record) => {
          if (record.isSummaryField) {
            return <b>{`Total: ${formatCurrency(v)}`}</b>;
          }
          return formatCurrency(v);
        },
        onCell: r => ({ colSpan: r.isRowHeader || r.isBlankField ? 0 : 1 }),
      },
    ];
  }, []);

  const onSearch = ({ fromDate, toDate }: any) => {
    setFilter({
      fromDate: formatApiDate(fromDate),
      toDate: formatApiDate(toDate),
    });
  };
  const onExportExcel = () => {
    searchForm.validateFields().then(res => {
      exportService.exportSaleWithGroupReport({ ...res, fromDate: formatApiDate(res.fromDate), toDate: formatApiDate(res.fromDate) }).toExcel();
    });
  };
  const onExportPdf = () => {
    searchForm.validateFields().then(res => {
      exportService.exportSaleWithGroupReport({ ...res, fromDate: formatApiDate(res.fromDate), toDate: formatApiDate(res.fromDate) }).toPdf();
    });
  };

  return (
    <MainContent
      title={t(PAGE_TITLE)}
      extraBtns={
        <Space>
          <ExportPdfBtn onClick={onExportPdf} />
          <ExportExcelBtn onClick={onExportExcel} />
        </Space>
      }
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <Form
          layout="inline"
          className="nta-custom-form"
          form={searchForm}
          colon={false}
          onFinish={onSearch}
          initialValues={filter}
          style={{ marginTop: 10 }}
        >
          <Row gutter={ROW_GUTTER}>
            <Col md={24} xl={24} xxl={24}>
              <Space>
                <Form.Item name="fromDate" label={t('From date')} rules={[{ required: true, message: '' }]}>
                  <AppDatePicker style={{ width: 150 }} allowClear={false} />
                </Form.Item>
                <Form.Item name="toDate" label={t('To Date')} rules={[{ required: true, message: '' }]}>
                  <AppDatePicker style={{ width: 150 }} allowClear={false} />
                </Form.Item>
                <Form.Item style={{ textAlign: 'left' }}>
                  <Space>
                    <Button type="primary" htmlType="submit">
                      <SearchOutlined />
                      {t('zTable.search')}
                    </Button>
                    <Button type="ghost" htmlType="reset">
                      <CloseOutlined />
                      {t('zTable.reset')}
                    </Button>
                  </Space>
                </Form.Item>
              </Space>
            </Col>
          </Row>
        </Form>

        <Table
          key={tableKey}
          className="transport-report-table"
          loading={loading}
          size="small"
          bordered
          columns={columns}
          expandable={{
            rowExpandable: record => !!record.isRowHeader,
            fixed: 'left',
            defaultExpandAllRows: true,
            childrenColumnName: 'childData',
          }}
          dataSource={data}
          pagination={false}
          rowClassName={v => (v.isSummaryField ? 'summary-row' : '')}
        />
      </div>
    </MainContent>
  );
};
export default SaleWithGroupReportPage;
